/**
 * useAuth Composable
 * Provides authentication functionality and reactive state
 */
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth.store.js';
import { executeMutation, executeQuery } from '../services/appsync-client.js';
import { SIGN_IN } from '../graphql/signIn.js';
import { GET_ME } from '../graphql/getMe.js';
export function useAuth() {
    const router = useRouter();
    const authStore = useAuthStore();
    // Loading states
    const signInLoading = ref(false);
    const meLoading = ref(false);
    // Error states
    const signInError = ref(null);
    const meError = ref(null);
    /**
     * Sign in user
     */
    async function signIn(email, password) {
        try {
            authStore.setLoading(true);
            authStore.setError(null);
            signInLoading.value = true;
            signInError.value = null;
            const result = await executeMutation(SIGN_IN, {
                email,
                password,
            });
            if (result?.signIn) {
                authStore.setTokens(result.signIn);
                // Fetch user info
                await refetchMe();
                // Redirect to home after successful sign in
                await router.push('/');
            }
            else {
                throw new Error('Sign in failed: No data returned');
            }
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Sign in failed';
            signInError.value = err instanceof Error ? err : new Error(errorMessage);
            authStore.setError(errorMessage);
            throw err;
        }
        finally {
            authStore.setLoading(false);
            signInLoading.value = false;
        }
    }
    /**
     * Refetch user info
     */
    async function refetchMe() {
        try {
            meLoading.value = true;
            meError.value = null;
            // Disabled for O365 auth - user info comes from ID token
            // This is kept for compatibility but won't actually fetch
            const result = await executeQuery(GET_ME);
            if (result?.me) {
                authStore.setUser(result.me);
            }
        }
        catch (err) {
            meError.value = err instanceof Error ? err : new Error('Failed to fetch user info');
            // Ignore errors since this query may not be implemented (O365 auth)
        }
        finally {
            meLoading.value = false;
        }
    }
    /**
     * Sign out user
     */
    async function signOut() {
        authStore.clearAuth();
        await router.push('/');
    }
    /**
     * Check if user has specific role
     */
    function hasRole(role) {
        return authStore.userRoles.includes(role);
    }
    /**
     * Check if user has any of the specified roles
     */
    function hasAnyRole(roles) {
        return roles.some((role) => authStore.userRoles.includes(role));
    }
    /**
     * Check if user has all specified roles
     */
    function hasAllRoles(roles) {
        return roles.every((role) => authStore.userRoles.includes(role));
    }
    return {
        // State
        user: computed(() => authStore.user),
        isAuthenticated: computed(() => authStore.isAuthenticated),
        isLoading: computed(() => authStore.isLoading),
        error: computed(() => authStore.error),
        isTokenValid: computed(() => authStore.isTokenValid),
        userRoles: computed(() => authStore.userRoles),
        userGroups: computed(() => authStore.userGroups),
        // Loading states
        signInLoading: computed(() => signInLoading.value),
        meLoading: computed(() => meLoading.value),
        // Error states
        signInError: computed(() => signInError.value),
        meError: computed(() => meError.value),
        // Actions
        signIn,
        signOut,
        refetchMe,
        hasRole,
        hasAnyRole,
        hasAllRoles,
    };
}
//# sourceMappingURL=useAuth.js.map